// GENERATED — DO NOT HAND-EDIT.
//
// Source of truth: specs/tier-05-experience/campaign.entity-hint.yaml
// Generator: tools/v2-gen/v2gen.py
// Regenerate via: make v2-generate-screens
//
// Hand-edits to non-override-block content WILL be lost on next
// regeneration. See specs/_product/dashboard-v2-page-generator.md §5
// for override blocks (imports / helpers / header-actions).

import type { ListPageConfig } from "../../components/composition/ListPage";
import { campaignsApi } from "../../api/campaigns";
import type { Campaign } from "../../api/types";
// override-block:imports BEGIN
import type { CampaignStatus } from "../../api/types";
// CHG-1294 — per-page RBAC gate wiring (consumes GATE_CATALOG).
import { GATE_CATALOG } from "../../auth/gateCatalog";
import { toGate } from "../../auth/toGateProp";
// CHG-1330 — bulk-tag UX (closes DRIFT-648).
import { openBulkTagDialog } from "../../lib/bulk-tag-dialog";
// override-block:imports END

// override-block:helpers BEGIN
const STATUS_LABEL: Record<CampaignStatus, string> = {
  draft: "Draft",
  active: "Active",
  // CHG-CAMP-005 (R13) — removed invalid "paused" (not a campaign status).
  completed: "Completed",
  expired: "Expired",
  archived: "Archived",
};
// override-block:helpers END

export const campaignListConfig: ListPageConfig<Campaign> = {
  title: "Campaigns",
  subtitle: "Fundraising campaigns — track pace against goal and surface ones that need attention.",
  queryKey: ["campaigns", "list"] as const,
  queryFn: ({ page, pageSize, sortField, sortDirection, search }) =>
    campaignsApi.listCampaigns({ page, pageSize, sortField, sortDirection, search }),
  defaultSort: { field: "created_at", direction: "desc" },
  pageSize: 50,
  rowHref: "/campaigns/{id}",
  search: { enabled: true, placeholder: "Search campaigns by name or slug…" },
  ambientRow: {
    stageField: "stage",
    entity: "campaign",
    activeStages: ["at_risk", "off_pace", "final_push"],
  },
  emptyState: {
    title: "No campaigns yet",
    subtitle: "Launch your first campaign to start tracking fundraising goals and donor progress.",
  },
  headerActions: [
    // override-block:header-actions BEGIN
    { id: "edit", label: "New campaign", variant: "primary", permissionGate: toGate(GATE_CATALOG.campaign_list["edit"], "disable") },
    { id: "duplicate", label: "Duplicate", variant: "ghost", permissionGate: toGate(GATE_CATALOG.campaign_list["duplicate"], "hide") },
    // override-block:header-actions END
  ],
  bulkActions: [
    {
      id: "tag",
      label: "Tag selected",
      variant: "primary",
      permission: "alora-giving.tag.attach",
      permissionFallback: "hide",
      onAction:
        // override-block:bulk-action-tag BEGIN
        (ids) => openBulkTagDialog({ entityType: "campaign", ids: ids.map(String) }),
        // override-block:bulk-action-tag END
    },
  ],
  columns: [
    { field: "name", label: "Name", type: "text", sortable: true },
    { field: "goal_amount_minor_units", label: "Goal", type: "currency", currencyField: "currency", sortable: true },
    { field: "raised_minor_units", label: "Raised", type: "currency", currencyField: "currency", sortable: true },
    // override-block:column-progress-pct BEGIN
    {
      field: "progress_pct",
      label: "Progress",
      type: "custom",
      sortable: true,
      render: (row) => `${(row.progress_pct ?? 0).toFixed(1)}%`,
    },
    // override-block:column-progress-pct END
    { field: "start_date", label: "Start", type: "date", sortable: true },
    { field: "end_date", label: "End", type: "date", sortable: true },
    // override-block:column-status BEGIN
    {
      field: "status",
      label: "Status",
      type: "custom",
      render: (row) => STATUS_LABEL[row.status] ?? row.status,
    },
    // override-block:column-status END
    { field: "stage", label: "Stage", type: "stage-chip", entity: "campaign" },
  ],
};
